import { useMemo } from 'react';
import {
  SALES_METROS_PAGE_SIZE,
  filterSalesMetroRows,
  toSalesMetroRows,
  useSalesMetroByZip,
  useSalesMetrosForManagement,
} from '../../../../domain/SalesMetros';
import { useGetAllInternalUsers } from '../../../../domain/Users';
import { paginate } from '../../../../utils/paginate';

/**
 * One page of the Sales Metros table (GOTU-6009), searched and paged
 * client-side over two whole reads — every metro and every admin — joined by
 * `toSalesMetroRows`. A keystroke changes what is shown, never what is fetched,
 * with one exception: a whole 5-digit term also asks organization-service which
 * metro owns that zip, since only the server knows a metro's zips.
 *
 * That lookup's failure is deliberately not the page's: it means "no extra
 * match", and the name and admin matches stay on screen. The global query
 * toaster still reports it.
 *
 * @param {Object} [params]
 * @param {string} [params.search=''] - Metro name, admin name or a 5-digit zip
 * @param {number} [params.page=1] - 1-based page
 */
export const useSalesMetrosPage = ({ search = '', page = 1 } = {}) => {
  const term = search.trim();

  const metrosQuery = useSalesMetrosForManagement();
  const adminsQuery = useGetAllInternalUsers();
  const zipQuery = useSalesMetroByZip(term);

  const zipMatchId = zipQuery.metro?.id ?? null;

  const filtered = useMemo(
    () =>
      filterSalesMetroRows(toSalesMetroRows(metrosQuery.metros, adminsQuery.admins), {
        search: term,
        zipMatchId,
      }),
    [metrosQuery.metros, adminsQuery.admins, term, zipMatchId],
  );

  return {
    rows: paginate(filtered, page, SALES_METROS_PAGE_SIZE),
    filteredCount: filtered.length,
    pageSize: SALES_METROS_PAGE_SIZE,
    admins: adminsQuery.admins,
    isPending: metrosQuery.isPending || adminsQuery.isPending,
    isFetching: metrosQuery.isFetching || adminsQuery.isFetching || zipQuery.isFetching,
    // Without the admin list no row can name its admins and the edit dialog
    // has nothing to offer, so either read failing is the page failing.
    isError: metrosQuery.isError || adminsQuery.isError,
  };
};
